/**
 * Auth 模块（#89098）
 *
 * 数据面 `/.cloud/auth/v1/**`。
 *
 * **四个模块里唯一自研的**。Auth 也是唯一不能纯透传的能力：登录响应里 provider
 * 的 token 必须被换成我方自签 session，续期在服务端完成（§5.3 / §6.0）。
 *
 * ============================================================================
 * 本模块是整个客户端的**身份唯一来源**
 *
 * database / storage 的请求身份不由它们自己管 —— client.ts 把
 * `() => auth.getAccessToken()` 交给共享 fetch，于是登录一次其它模块自动带上
 * 身份，登出一次其它模块自动变匿名。调用方不需要（也不应该）手动搬 token。
 *
 * 这也意味着 `getAccessToken()` 在**每个** database/storage 请求前都会被调用、
 * 且可能被高并发调用 —— 它必须便宜且幂等。过期续期的并发保护在
 * session-manager.ts。
 * ============================================================================
 */

import type { CloudError, CloudResult } from '../../http/errors.js';
import { CLOUD_MODULE_PATHS } from '../../paths.js';
import type { CloudRuntimeConfig } from '../../types.js';
import {
    computeS256Challenge,
    createCodeVerifier,
    hasWebCrypto,
    WorkBuddyPkceStore,
} from './pkce.js';
import {
    AUTH_PATHS,
    normalizeHttpError,
    normalizeNetworkError,
    parseSession,
    parseUser,
} from './protocol.js';
import { SessionManager } from './session-manager.js';
import { resolveStorage, SessionStore } from './session-store.js';
import type {
    CloudAuthEvent,
    CloudAuthStateCallback,
    CloudAuthStorage,
    CloudSession,
    CloudUser,
    OAuthRedirect,
    OAuthSignInOptions,
    OtpChallenge,
    OtpCredentials,
    OtpSignInChallenge,
    PasswordResetChallenge,
    PasswordCredentials,
    PhonePasswordCredentials,
    ResetPasswordWithOldCredentials,
    SignUpCredentials,
    VerifyOtpParams,
} from './types.js';
import { resolveAuthIdentifier } from './identifier.js';

/** 错误文案上限，按 Unicode 码点计（不是 UTF-16 code unit）。 */
const OAUTH_ERROR_MESSAGE_MAX = 200;

function truncateMessage(message: string, max: number): string {
    const chars = Array.from(message);
    return chars.length <= max ? message : `${chars.slice(0, max).join('')}…`;
}

/** 只认 `error`。单独出现的 `error_description` 不是拒绝，不能当成失败。 */
function oauthProviderRejectMessage(params: URLSearchParams): string | null {
    const error = params.get('error');
    if (!error) {
        return null;
    }
    const description = params.get('error_description');
    const message = description
        ? `oauth provider rejected the request: ${error}: ${description}`
        : `oauth provider rejected the request: ${error}`;
    return truncateMessage(message, OAUTH_ERROR_MESSAGE_MAX);
}

export interface AuthModuleOptions {
    /** 凭据存储。缺省用 localStorage（探测失败退回内存）。 */
    storage?: CloudAuthStorage;
}

export class AuthModule {
    /** 该模块的数据面基址。 */
    readonly baseUrl: string;

    private readonly sessions: SessionManager;
    private readonly storage: CloudAuthStorage;
    private readonly oauthRelayBaseUrl: string;
    /** WorkBuddy PKCE 证明的暂存处，按应用隔离。 */
    private readonly workBuddyPkce: WorkBuddyPkceStore;
    /**
     * 正在进行中的 WorkBuddy 兑换，按 state 单飞。
     *
     * 只靠 `isConsumed(state)` 挡不住**并发**：两次调用会在第一次写下成功标记之前
     * 都读到「未消费」，于是第二次拿不到已被清掉的 verifier，报
     * 「no local PKCE verifier」—— 登录其实成功了，却同时弹一条错误。
     * 应用侧只要在 StrictMode 下挂两个 effect，或路由守卫与入口各调一次，就会撞上。
     */
    private workBuddyExchanges = new Map<string, Promise<CloudResult<CloudSession | null>>>();
    private readonly listeners = new Map<number, CloudAuthStateCallback>();
    private nextListenerId = 1;

    /**
     * @param fetch 必须是**不带 session provider** 的 fetch。
     *
     * 原因：本模块的 `/v1/token` 就是续期端点；若它走「取 token（必要时先续期）」
     * 那条出口，续期就会调用自己 —— 死锁。CloudBase 的 OAuth2Client 为同一个
     * 问题留了逃生口，注释里直接写了 "may cause deadlock during initialization"。
     * 装配见 client.ts。
     */
    constructor(
        config: CloudRuntimeConfig,
        private readonly fetch: typeof globalThis.fetch,
        opts: AuthModuleOptions = {},
    ) {
        this.baseUrl = `${config.endpoint}${CLOUD_MODULE_PATHS.auth}`;
        this.oauthRelayBaseUrl = config.oauthRelayBaseUrl;
        this.storage = resolveStorage(opts.storage);
        this.workBuddyPkce = new WorkBuddyPkceStore(config.publishableKey);
        this.sessions = new SessionManager({
            store: new SessionStore(this.storage, config.publishableKey),
            refresh: (handle) => this.exchangeRefreshHandle(handle),
            emit: (event, session) => this.notify(event, session),
            isCredentialsRejected: (err) => isCloudError(err) && rejectedCredentials(err),
        });
    }

    // ------------------------------------------------------------------
    // 供 client.ts 装配共享 fetch 用
    // ------------------------------------------------------------------

    /**
     * 取一个当前可用的 access token，必要时先续期。无会话返回 undefined。
     *
     * database / storage 的每个请求都会调它 —— 返回 undefined 表示匿名，
     * 那是合法状态不是错误（服务端按 anon 角色处理）。
     */
    async getAccessToken(): Promise<string | undefined> {
        const session = await this.sessions.ensure();
        return session?.accessToken;
    }

    // ------------------------------------------------------------------
    // 登录
    // ------------------------------------------------------------------

    /** 邮箱或手机号 + 密码登录。 */
    async signInWithPassword(
        credentials: PasswordCredentials | PhonePasswordCredentials,
    ): Promise<CloudResult<CloudSession>> {
        if (!credentials.password) {
            return fail(badRequest('signInWithPassword requires a password'));
        }
        const identifier = resolveAuthIdentifier(credentials, 'signInWithPassword');
        if (identifier.error) {
            return fail(identifier.error);
        }
        return this.postForSession(AUTH_PATHS.signIn, {
            username: identifier.data.username,
            password: credentials.password,
        });
    }

    /**
     * 小程序 wx.login 的 code 换 Genie session。
     * 试用与正式是两套独立小程序，必须带当前账号的 appid
     *（`wx.getAccountInfoSync().miniProgram.appId`）。
     * 成功后 session.user.id 为 TCB custom uid：`wx:` + 微信 openid（长度需 ≤32）。
     */
    async signInWithWechat(code: string, appid: string): Promise<CloudResult<CloudSession>> {
        if (!code) {
            return fail(badRequest('signInWithWechat requires code'));
        }
        if (!appid) {
            return fail(badRequest('signInWithWechat requires appid'));
        }
        return this.postForSession(AUTH_PATHS.loginWechat, { code, appid });
    }

    /**
     * 注册。
     *
     * 上游要求先完成验证码验证，所以必须带一个 `verificationToken`（来自
     * `verifyOtp`）。只有用户名+密码的注册会被上游明确拒绝。
     *
     * 账号标识用 `email` 或 `phone`（二选一，与发码渠道一致）。
     */
    async signUp(
        credentials: SignUpCredentials & { verificationToken: string },
    ): Promise<CloudResult<CloudSession>> {
        const identifier = resolveAuthIdentifier(credentials, 'signUp');
        if (identifier.error) {
            return fail(identifier.error);
        }
        if (!credentials.verificationToken) {
            return fail(
                badRequest(
                    'signUp requires a verificationToken; run sendOtp + verifyOtp first ' +
                        '(the provider rejects username+password-only signup)',
                ),
            );
        }
        const body: Record<string, unknown> = {
            verification_token: credentials.verificationToken,
        };
        if (identifier.data.kind === 'phone') {
            body.phone_number = identifier.data.username;
        } else {
            body.email = identifier.data.username;
        }
        if (credentials.password) body.password = credentials.password;
        return this.postForSession(AUTH_PATHS.signUp, body);
    }

    /**
     * 发验证码（邮箱或短信，由 `credentials` 传的字段决定）。
     *
     * 这一步**不产生会话**，只返回一个 `verificationId`。发码由上游完成，
     * 我方不实现发码逻辑。
     *
     * 返回的 `isExistingUser` 决定验码之后走登录还是注册 —— 该判断由上游给出，
     * SDK 不猜。
     *
     * 两条渠道的请求体差异是刻意的（runtime-auth-design §3.1）：
     * - 邮箱：`{ email, usage: 'email' }`，与上游 `VerificationUsage.EMAIL` 的值一致，区分大小写。
     * - 手机：`{ phone_number, target: 'ANY' }` —— **不带 `usage`**（普通短信登录
     *   发码时省略即可，上游没有对应的短信枚举值），`target: 'ANY'` 表示新老用户
     *   都允许发码。
     */
    async sendOtp(credentials: OtpCredentials): Promise<CloudResult<OtpChallenge>> {
        const identifier = resolveAuthIdentifier(credentials, 'sendOtp');
        if (identifier.error) {
            return fail(identifier.error);
        }
        const body: Record<string, unknown> = {};
        if (identifier.data.kind === 'phone') {
            body.phone_number = identifier.data.username;
            body.target = 'ANY';
        } else {
            body.email = identifier.data.username;
            body.usage = 'email';
            if (credentials.emailRedirectTo) {
                body.email_redirect_to = credentials.emailRedirectTo;
            }
        }

        const res = await this.request(AUTH_PATHS.verification, { method: 'POST', body });
        if (res.error) {
            return fail(res.error);
        }
        const payload = (res.data ?? {}) as Record<string, unknown>;
        const verificationId =
            typeof payload.verification_id === 'string' ? payload.verification_id : '';
        if (!verificationId) {
            return fail(badRequest('provider returned no verification_id'));
        }
        return ok({ verificationId, isExistingUser: payload.is_user === true });
    }

    /** gotrue 风格的 OTP 入口：发码后由 challenge.verify 完成登录。 */
    async signInWithOtp(
        credentials: OtpCredentials,
    ): Promise<CloudResult<OtpSignInChallenge>> {
        // 必须与 sendOtp 用同一套标识解析：JS 表单常保留 `phone: ''` / `null`，
        // 单独判断 `phone !== undefined` 会把邮箱发码的 challenge 存成空手机号。
        const resolved = resolveAuthIdentifier(credentials, 'signInWithOtp');
        if (resolved.error) {
            return fail(resolved.error);
        }
        const sent = await this.sendOtp(credentials);
        if (sent.error) {
            return fail(sent.error);
        }
        const { verificationId, isExistingUser } = sent.data;
        const identifier =
            resolved.data.kind === 'phone'
                ? { phone: resolved.data.username }
                : { email: resolved.data.username };
        return ok({
            verificationId,
            isExistingUser,
            isUser: isExistingUser,
            verify: ({ token, password }) =>
                this.verifyOtp({
                    verificationId,
                    token,
                    ...identifier,
                    isExistingUser,
                    password,
                }),
        });
    }

    /**
     * 验码并登录（或注册后登录）。
     *
     * 两步：先用验证码换一个**一次性** `verification_token`，再用它换会话。
     * 中间那个 token 不是会话 —— 拿它当 Bearer 会被拒。
     *
     * 走登录还是注册由 `isExistingUser` 决定（`sendOtp` 的返回值）。刻意不做
     * 「先试登录失败再试注册」的兜底：那会把「密码错」这类真实错误掩盖成一次
     * 莫名的注册尝试。
     *
     * 登录侧邮箱与手机号共用 `username` 字段 —— 上游 `SignInRequest.username`
     * 承载邮箱/手机/用户名三种形态（runtime-auth-design §3.2）。
     */
    async verifyOtp(
        params: VerifyOtpParams & { isExistingUser: boolean },
    ): Promise<CloudResult<CloudSession>> {
        const identifier = resolveAuthIdentifier(params, 'verifyOtp');
        if (identifier.error) {
            return fail(identifier.error);
        }
        if (!params.verificationId || !params.token) {
            return fail(
                badRequest('verifyOtp requires verificationId and token'),
            );
        }
        const verified = await this.request(AUTH_PATHS.verificationVerify, {
            method: 'POST',
            body: {
                verification_id: params.verificationId,
                verification_code: params.token,
            },
        });
        if (verified.error) {
            return fail(verified.error);
        }
        const vt = (verified.data ?? {}) as Record<string, unknown>;
        const verificationToken =
            typeof vt.verification_token === 'string' ? vt.verification_token : '';
        if (!verificationToken) {
            return fail(badRequest('provider returned no verification_token'));
        }

        if (params.isExistingUser) {
            return this.postForSession(AUTH_PATHS.signIn, {
                username: identifier.data.username,
                verification_token: verificationToken,
            });
        }
        const signUpCredentials: SignUpCredentials =
            identifier.data.kind === 'phone'
                ? { phone: identifier.data.value, password: params.password }
                : { email: identifier.data.value, password: params.password };
        return this.signUp({ ...signUpCredentials, verificationToken });
    }

    // ------------------------------------------------------------------
    // OAuth
    // ------------------------------------------------------------------

    /**
     * 发起第三方登录，返回该把用户送去的地址。
     *
     * 走 Cloud Backend Service OAuth Relay。IdP 回调白名单只登记平台统一域名。
     * SDK 不自己跳转：返回 url 交调用方 `location.assign`。
     *
     * `google` 已下线，仅存量应用；新应用用 `wechat` 做网页扫码登录，或用
     * `workbuddy` 做 WorkBuddy 账号登录。
     *
     * `workbuddy` 会在本地生成 PKCE 证明并存入当前标签页的 sessionStorage，所以
     * **必须由用户手势触发同一标签页的跳转**：另开标签页或新窗口打开返回的 url，
     * 证明留在原标签页里，回调页读不到它，兑换必然失败。
     */
    async signInWithOAuth(options: OAuthSignInOptions): Promise<CloudResult<OAuthRedirect>> {
        if (!this.oauthRelayBaseUrl) {
            return fail(badRequest('signInWithOAuth requires oauthRelayBaseUrl'));
        }
        if (
            options.provider !== 'google' &&
            options.provider !== 'wechat' &&
            options.provider !== 'workbuddy'
        ) {
            return fail(badRequest('当前 SDK 不支持该登录方式，请检查 SDK 版本与登录配置。'));
        }
        const redirectTo = options.redirectTo ?? currentPageUrl();
        if (!redirectTo) {
            return fail(
                badRequest('signInWithOAuth requires redirectTo outside a browser environment'),
            );
        }
        return this.startRelayOAuth(options, redirectTo);
    }

    /** 回调输入的 query 归一：接受完整 URL 或裸 query。 */
    private parseCallbackQuery(input: string): CloudResult<URLSearchParams> {
        try {
            const query = /^https?:\/\//i.test(input) ? new URL(input).search : input;
            return ok(new URLSearchParams(query));
        } catch {
            return fail(badRequest('oauth callback URL is invalid'));
        }
    }

    /**
     * 完成第三方登录：读回调 URL 上的参数换会话。
     *
     * 在回调落地页调一次即可。`code` 缺失时返回 `null` 数据而**不是**错误 ——
     * 落地页可能被直接访问（用户收藏了它），那不是失败。
     *
     * @deprecated Google 登录已下线，新应用不应再调用该方法。
     */
    async handleOAuthCallback(search?: string): Promise<CloudResult<CloudSession | null>> {
        const parsed = this.parseCallbackQuery(search ?? currentPageSearch());
        if (parsed.error || !parsed.data) {
            return fail(parsed.error ?? badRequest('oauth callback URL is invalid'));
        }
        const params = parsed.data;
        // 上游的失败也从 query 回来（用户点了「拒绝授权」）。
        const oauthError = oauthProviderRejectMessage(params);
        if (oauthError) {
            return fail({
                kind: 'unauthenticated',
                message: oauthError,
                status: 0,
            });
        }
        const code = params.get('code');
        if (!code) {
            return ok(null);
        }
        const state = params.get('state') ?? '';
        if (!state) {
            return fail(badRequest('oauth callback requires state'));
        }
        const providerId = params.get('provider_id');
        if (providerId !== 'custom-oauth') {
            return fail(badRequest('oauth callback requires provider_id=custom-oauth'));
        }

        const body: Record<string, unknown> = {
            provider_id: providerId,
            provider_code: code,
            state,
            provider_params: { state },
        };
        return this.postForSession(AUTH_PATHS.signInWithProvider, body);
    }

    /**
     * 网页微信扫码回调：用 Relay 带回的 code/state 换 Genie session。
     *
     * 仅用于 **微信扫码** 落地页。Google 存量应用必须继续调用
     * `handleOAuthCallback`（custom-oauth 路径）。两者不可互换：本方法会拒绝
     * `provider != "wechat"` 的回调，报错看起来像前端传错参数。
     */
    async handleWechatWebCallback(search?: string): Promise<CloudResult<CloudSession | null>> {
        const parsed = this.parseCallbackQuery(search ?? currentPageSearch());
        if (parsed.error || !parsed.data) {
            return fail(parsed.error ?? badRequest('oauth callback URL is invalid'));
        }
        const params = parsed.data;
        const oauthError = oauthProviderRejectMessage(params);
        if (oauthError) {
            return fail({
                kind: 'unauthenticated',
                message: oauthError,
                status: 0,
            });
        }
        const code = params.get('code');
        if (!code) {
            return ok(null);
        }
        const state = params.get('state') ?? '';
        if (!state) {
            return fail(badRequest('oauth callback requires state'));
        }
        const provider = params.get('provider');
        if (provider && provider !== 'wechat') {
            return fail(badRequest('wechat callback requires provider=wechat'));
        }
        if (params.get('provider_id') === 'custom-oauth') {
            return fail(badRequest('wechat callback must not use provider_id=custom-oauth'));
        }
        return this.postForSession(AUTH_PATHS.loginWechatWeb, { code, state });
    }

    /**
     * WorkBuddy 统一登录回调：用 Relay 带回的 code/state 加本地 verifier 换
     * Genie session。
     *
     * ---------------------------------------------------------------------------
     * 在**登录守卫之前**无条件调用一次
     * ---------------------------------------------------------------------------
     *
     * 回调落地的那一刻还没有会话，守卫先跑就会把用户弹回登录页、顺手把 URL 上的
     * code/state 丢掉，表现是「点了登录转一圈又回到登录页」。
     *
     * 返回 `{ data: null, error: null }` 表示**这不是一次 WorkBuddy 回调**（普通
     * 页面访问、用户收藏了落地页），不是失败：照常渲染未登录状态即可。
     *
     * 失败一律不建会话：用户在授权页点了拒绝、本地证明缺失（换了标签页、清了
     * sessionStorage）、回调参数不合法，都只返回 error。
     *
     * 兑换成功后 URL 上的 `code` / `state` / `provider` 应由调用方清掉
     * （`history.replaceState`），业务参数如 `sharecode` 要保留。清理前后重复调用
     * 本方法都不会再次兑换：并发的那次合流到同一个 promise、拿到同一个结果，
     * 串行的那次按「成功已记下」返回空。
     */
    async handleWorkBuddyCallback(search?: string): Promise<CloudResult<CloudSession | null>> {
        const parsed = this.parseCallbackQuery(search ?? currentPageSearch());
        if (parsed.error || !parsed.data) {
            return fail(parsed.error ?? badRequest('oauth callback URL is invalid'));
        }
        const params = parsed.data;
        const provider = params.get('provider');
        // 先判 provider：微信/Google 的回调也会带 code+state，不先分流就会被这里
        // 当成缺证明的 WorkBuddy 回调，还会顺手把别人的事务判成失败。
        if (provider && provider !== 'workbuddy') {
            return ok(null);
        }
        const oauthError = oauthProviderRejectMessage(params);
        if (oauthError) {
            // 事务已经废了，证明留着没用，顺手清掉以免下一次授权读到旧值。
            this.workBuddyPkce.clearVerifier();
            return fail({
                kind: 'unauthenticated',
                message: oauthError,
                status: 0,
            });
        }
        const code = params.get('code');
        if (!code) {
            return ok(null);
        }
        const state = params.get('state') ?? '';
        if (!state) {
            return fail(badRequest('workbuddy callback requires state'));
        }
        if (params.get('provider_id') === 'custom-oauth') {
            return fail(badRequest('workbuddy callback must not use provider_id=custom-oauth'));
        }
        // 同一事务的重复调用：会话在第一次就建好了，这里不能再打一次兑换
        // （服务端 state 一次性，第二次必然 400，看起来像登录失败）。
        if (this.workBuddyPkce.isConsumed(state)) {
            return ok(null);
        }
        // 并发重复调用：串行时靠上面那条已消费标记拦下，并发时两边都还没看到标记，
        // 必须在这里合流 —— 否则后到的那次读不到已清掉的 verifier，会在登录成功的
        // 同时报一条「缺本地证明」。合流后两边拿到同一次兑换的同一个结果。
        const inflight = this.workBuddyExchanges.get(state);
        if (inflight) {
            return inflight;
        }
        const verifier = this.workBuddyPkce.readVerifier();
        if (!verifier) {
            return fail(
                badRequest(
                    'workbuddy callback has no local PKCE verifier; sign in again in this tab',
                ),
            );
        }
        // 兑换前就清掉：服务端的 state 也是一次性的，保留一个用过的 verifier 只会
        // 让后续调用拿着它去撞一个已消费的事务。
        this.workBuddyPkce.clearVerifier();
        const task = this.exchangeWorkBuddyCode(code, state, verifier);
        this.workBuddyExchanges.set(state, task);
        try {
            return await task;
        } finally {
            // 成功已记在 isConsumed 里；失败则让下一次调用能重新报出同一个错误，
            // 而不是永久复用一个失败的 promise。
            this.workBuddyExchanges.delete(state);
        }
    }

    /** 真正那一次兑换，单飞逻辑在 {@link handleWorkBuddyCallback}。 */
    private async exchangeWorkBuddyCode(
        code: string,
        state: string,
        verifier: string,
    ): Promise<CloudResult<CloudSession | null>> {
        const result = await this.postForSession(AUTH_PATHS.loginWorkBuddy, {
            code,
            state,
            code_verifier: verifier,
        });
        if (result.data) {
            this.workBuddyPkce.markConsumed(state);
        }
        return result;
    }

    /** 忘记密码：发码，验证码通过后更新密码并自动登录。 */
    async resetPasswordForEmail(
        email: string,
    ): Promise<CloudResult<PasswordResetChallenge>> {
        if (!email) {
            return fail(badRequest('resetPasswordForEmail requires email'));
        }
        const sent = await this.request(AUTH_PATHS.verification, {
            method: 'POST',
            body: { email, usage: 'PASSWORD_RESET', target: 'USER' },
        });
        if (sent.error) {
            return fail(sent.error);
        }
        const payload = (sent.data ?? {}) as Record<string, unknown>;
        const verificationId =
            typeof payload.verification_id === 'string' ? payload.verification_id : '';
        if (!verificationId) {
            return fail(badRequest('provider returned no verification_id'));
        }
        return ok({
            updateUser: async ({ nonce, password }) => {
                const verified = await this.request(AUTH_PATHS.verificationVerify, {
                    method: 'POST',
                    body: {
                        verification_id: verificationId,
                        verification_code: nonce,
                    },
                });
                if (verified.error) {
                    return fail(verified.error);
                }
                const verifiedPayload = (verified.data ?? {}) as Record<string, unknown>;
                const verificationToken =
                    typeof verifiedPayload.verification_token === 'string'
                        ? verifiedPayload.verification_token
                        : '';
                if (!verificationToken) {
                    return fail(badRequest('provider returned no verification_token'));
                }
                const reset = await this.request(AUTH_PATHS.resetPassword, {
                    method: 'POST',
                    body: {
                        email,
                        new_password: password,
                        verification_token: verificationToken,
                    },
                });
                if (reset.error) {
                    return fail(reset.error);
                }
                const signedIn = await this.signInWithPassword({ email, password });
                if (!signedIn.error) {
                    this.notify('PASSWORD_RECOVERY', signedIn.data);
                }
                return signedIn;
            },
        });
    }

    /** 登录后使用旧密码进行 sudo 校验并更新密码。 */
    async resetPasswordForOld(
        credentials: ResetPasswordWithOldCredentials,
    ): Promise<CloudResult<CloudSession>> {
        const token = await this.getAccessToken();
        if (!token) {
            return fail({ kind: 'unauthenticated', message: 'no active session', status: 0 });
        }
        const sudo = await this.request(AUTH_PATHS.sudo, {
            method: 'POST',
            token,
            body: { password: credentials.oldPassword },
        });
        if (sudo.error) {
            return fail(sudo.error);
        }
        const payload = (sudo.data ?? {}) as Record<string, unknown>;
        const sudoToken = typeof payload.sudo_token === 'string' ? payload.sudo_token : '';
        if (!sudoToken) {
            return fail(badRequest('provider returned no sudo_token'));
        }
        const updated = await this.request(AUTH_PATHS.userPassword, {
            method: 'PATCH',
            token,
            body: { sudo_token: sudoToken, new_password: credentials.newPassword },
        });
        if (updated.error) {
            return fail(updated.error);
        }
        const session = await this.getSession();
        if (session.data) {
            this.notify('USER_UPDATED', session.data);
            return ok(session.data);
        }
        return fail(session.error ?? {
            kind: 'unauthenticated',
            message: 'no active session',
            status: 0,
        });
    }

    // ------------------------------------------------------------------
    // 会话
    // ------------------------------------------------------------------

    /**
     * 读当前会话。
     *
     * 会在临近过期时**顺手续期** —— 只读本地不续期的话，调用方拿到一个还剩
     * 3 秒有效期的 token 去发请求，到服务端就已经过期了。
     */
    async getSession(): Promise<CloudResult<CloudSession | null>> {
        try {
            return ok(await this.sessions.ensure());
        } catch (err) {
            return fail(isCloudError(err) ? err : normalizeNetworkError(err));
        }
    }

    /** 强制续期一次，不管是否临近过期。 */
    async refreshSession(): Promise<CloudResult<CloudSession | null>> {
        try {
            return ok(await this.sessions.forceRefresh());
        } catch (err) {
            return fail(isCloudError(err) ? err : normalizeNetworkError(err));
        }
    }

    /**
     * 读当前用户。
     *
     * 会发请求（`/v1/user/me`）而不是读本地 session —— 后者的 user 字段来自
     * 登录那一刻，可能已过时（别处改了昵称、或匿名已升级为实名）。
     */
    async getUser(): Promise<CloudResult<CloudUser>> {
        const token = await this.getAccessToken();
        if (!token) {
            return fail({ kind: 'unauthenticated', message: 'no active session', status: 0 });
        }
        const res = await this.request(AUTH_PATHS.userMe, { method: 'GET', token });
        if (res.error) {
            // 会话在服务端已失效（例如别处登出了）：本地也要清，否则界面会一直
            // 显示登录态却什么都做不了。
            if (rejectedCredentials(res.error)) {
                this.sessions.clear();
            }
            return fail(res.error);
        }
        return ok(parseUser(res.data));
    }

    /**
     * 登出。
     *
     * 无论服务端返回什么都清本地：服务端侧是**无条件销毁**的（转发上游之后就
     * 销毁，不看上游成败），本地留着只会让界面显示一个已经不能用的登录态。
     * 断网时也必须能登出。
     */
    async signOut(): Promise<CloudResult<null>> {
        const session = this.sessions.peek();
        if (session?.accessToken) {
            // 必须带 Authorization：不带的话服务端不知道要销毁哪个会话，
            // 会静默什么都不做。
            await this.request(AUTH_PATHS.signOut, {
                method: 'POST',
                body: {},
                token: session.accessToken,
            });
        }
        this.sessions.clear();
        return ok(null);
    }

    /**
     * 订阅登录态变化。返回取消订阅函数。
     *
     * 订阅时会**立即**回放一次 `INITIAL_SESSION`：没有它，调用方无法区分
     * 「还没初始化完」与「初始化完了但未登录」，首屏会闪一下登录页。
     */
    onAuthStateChange(callback: CloudAuthStateCallback): () => void {
        const id = this.nextListenerId++;
        this.listeners.set(id, callback);
        // 同步回放，不推到微任务：调用方常在首次 render 前订阅，延迟一拍就会
        // 多渲染一帧未登录状态。
        safeInvoke(callback, 'INITIAL_SESSION', this.sessions.peek());
        return () => void this.listeners.delete(id);
    }

    // ------------------------------------------------------------------
    // 内部
    // ------------------------------------------------------------------

    /**
     * 拼出平台 Relay 授权地址；state 由 Relay 自己签发、校验并一次性消费。
     */
    private async startRelayOAuth(
        options: OAuthSignInOptions,
        redirectTo: string,
    ): Promise<CloudResult<OAuthRedirect>> {
        // Cloud Backend Relay 自己签发、校验并一次性消费 state；应用只提供最终
        // callback URL，不能自行生成或覆盖 state。
        let callback: URL;
        try {
            callback = new URL(redirectTo);
        } catch {
            return fail(badRequest('signInWithOAuth requires an absolute redirectTo URL'));
        }
        const relay = new URL(`${this.oauthRelayBaseUrl}/authorize`);
        relay.searchParams.set('provider', options.provider);
        relay.searchParams.set('callback_url', callback.toString());

        if (options.provider === 'workbuddy') {
            // 失败就地返回，不拼出一个必然换不到会话的地址 —— 让用户走到回调页
            // 才报错，排查成本高得多。
            const proof = await this.createWorkBuddyProof();
            if (proof.error || !proof.data) {
                return fail(proof.error ?? badRequest('signInWithOAuth could not create PKCE'));
            }
            relay.searchParams.set('code_challenge', proof.data);
            relay.searchParams.set('code_challenge_method', 'S256');
        }

        return ok({ url: relay.toString() });
    }

    /**
     * 生成并暂存 WorkBuddy 的 PKCE 证明，返回要发给 Relay 的 challenge。
     *
     * 先存 verifier 再返回 challenge：顺序反了的话，调用方可能在 verifier 落盘前
     * 就跳走，回调页读到空值。
     */
    private async createWorkBuddyProof(): Promise<CloudResult<string>> {
        if (!hasWebCrypto()) {
            return fail(
                badRequest(
                    'workbuddy sign-in requires WebCrypto; it is unavailable in this runtime ' +
                        '(an insecure http:// origin disables it — serve the app over https)',
                ),
            );
        }
        if (!this.workBuddyPkce.usable()) {
            return fail(
                badRequest(
                    'workbuddy sign-in requires sessionStorage to keep the PKCE verifier across ' +
                        'the redirect; it is unavailable in this runtime',
                ),
            );
        }
        let verifier: string;
        let challenge: string;
        try {
            verifier = createCodeVerifier();
            challenge = await computeS256Challenge(verifier);
        } catch (err) {
            return fail(
                badRequest(
                    `workbuddy sign-in could not create a PKCE proof: ${describeError(err)}`,
                ),
            );
        }
        if (!this.workBuddyPkce.saveVerifier(verifier)) {
            return fail(badRequest('workbuddy sign-in could not persist the PKCE verifier'));
        }
        return ok(challenge);
    }

    /** 用我方句柄换新会话。这是 SessionManager 注入的续期执行体。 */
    private async exchangeRefreshHandle(handle: string): Promise<CloudSession> {
        // 刻意**不带** Authorization：句柄本身就是凭据；带上一个已过期的
        // access token 反而会在服务端的鉴权阶段先被 401 挡掉。
        const res = await this.request(AUTH_PATHS.token, {
            method: 'POST',
            body: { grant_type: 'refresh_token', refresh_token: handle },
        });
        if (res.error) {
            throw res.error;
        }
        return parseSession(res.data, Date.now());
    }

    /** POST 一个换签端点并把响应解析成会话。 */
    private async postForSession(
        path: string,
        body: unknown,
        opts: { headers?: Record<string, string> } = {},
    ): Promise<CloudResult<CloudSession>> {
        const res = await this.request(path, { method: 'POST', body, headers: opts.headers });
        if (res.error) {
            return fail(res.error);
        }
        try {
            return ok(this.sessions.commitSignIn(parseSession(res.data, Date.now())));
        } catch (err) {
            return fail(isCloudError(err) ? err : normalizeNetworkError(err));
        }
    }

    /** 发一次 auth 请求并归一错误。 */
    private async request(
        path: string,
        init: {
            method: string;
            body?: unknown;
            token?: string;
            headers?: Record<string, string>;
        },
    ): Promise<CloudResult<unknown>> {
        const headers: Record<string, string> = { ...init.headers };
        if (init.body !== undefined) {
            // Content-Type 必须是 JSON：服务端只在这个前提下才解析 body
            // （它要从里面找 refresh_token / redirect_uri 等字段）。
            headers['Content-Type'] = 'application/json';
        }
        if (init.token) {
            headers.Authorization = `Bearer ${init.token}`;
        }

        let response: Response;
        try {
            response = await this.fetch(this.baseUrl + path, {
                method: init.method,
                headers,
                body: init.body === undefined ? undefined : JSON.stringify(init.body),
            });
        } catch (err) {
            // fetch 层失败。403/404 在浏览器里也会走到这里（服务端对这两类刻意
            // 不回 CORS 头），所以这条分支不是「只有断网才进」。
            return fail(normalizeNetworkError(err));
        }

        const payload = await readBody(response);
        if (!response.ok) {
            return fail(normalizeHttpError(response.status, payload));
        }
        return ok(payload);
    }

    /** 广播事件。单个监听器抛错不能影响其它监听器与主流程。 */
    private notify(event: CloudAuthEvent, session: CloudSession | null): void {
        for (const cb of this.listeners.values()) {
            safeInvoke(cb, event, session);
        }
    }
}

// ----------------------------------------------------------------------
// 小工具
// ----------------------------------------------------------------------

function ok<T>(data: T): CloudResult<T> {
    return { data, error: null };
}

function fail<T>(error: CloudError): CloudResult<T> {
    return { data: null, error };
}

function badRequest(message: string): CloudError {
    return { kind: 'invalid-request', message, status: 0 };
}

/** 把未知异常压成一句可读原因，用于拼错误文案。 */
function describeError(err: unknown): string {
    return truncateMessage(
        err instanceof Error ? err.message : String(err),
        OAUTH_ERROR_MESSAGE_MAX,
    );
}

function isCloudError(v: unknown): v is CloudError {
    return !!v && typeof v === 'object' && typeof (v as CloudError).kind === 'string';
}

/** 该错误是否意味着本地凭据已废。 */
function rejectedCredentials(error: CloudError): boolean {
    return error.kind === 'unauthenticated' || error.code === 'invalid_grant';
}

async function readBody(response: Response): Promise<unknown> {
    const text = await response.text();
    if (!text) {
        return null;
    }
    try {
        return JSON.parse(text);
    } catch {
        // 非 JSON 响应（网关错误页等）：包一层，让上层仍能拿到原文。
        return { message: text };
    }
}

function safeInvoke(
    cb: CloudAuthStateCallback,
    event: CloudAuthEvent,
    session: CloudSession | null,
): void {
    try {
        cb(event, session);
    } catch {
        // 监听器里的异常是调用方的问题，不能让它中断登录/登出流程。
    }
}

/** 当前页地址（去掉 query 与 hash）—— OAuth 回调的缺省落地点。 */
function currentPageUrl(): string {
    const loc = globalThis.location;
    return loc ? loc.origin + loc.pathname : '';
}

function currentPageSearch(): string {
    return globalThis.location?.search ?? '';
}
